import { api, LightningElement, wire } from "lwc";
import classNames from "classnames";

import { EffectAdapter } from "common/effectAdapter";
import { assignedSlotNames } from "common/slot";
import {
  ResizeObserverAdapter,
  ResizeObserverEntry,
} from "common/resizeObserverAdapter";
import { BooleanAttr } from "common/helpers";
import { PAGE_SIDEBAR_WIDTH } from "common/mediaQueryAdapter";

export type Variant = "default" | "transparent";

export type CtaEvent = CustomEvent<{
  ctaHref: string | null;
  ctaLabel: string | null;
}>;

export default class extends LightningElement {
  @api contentMode: "default" | "padded" = "default";
  @api ctaDisabled: BooleanAttr = null;
  @api ctaLabel: string | null = null;
  @api ctaHref: string | null = null;
  @api ctaSpinner: BooleanAttr = null;
  @api ctaTarget: string | null = null;
  @api description: string | null = null;
  @api title: string = "";
  @api variant: Variant = "default";

  private width: number | null = null;

  get computedClassName() {
    return classNames("card", {
      "card--header": this.showHeader,
      "card--footer": this.showFooter,
    });
  }

  get computedClassNameFooterCta() {
    return classNames("footer-cta", {
      // CTA should be full-width when placed in a sidebar
      "footer-cta--full": this.width && this.width <= PAGE_SIDEBAR_WIDTH,
    });
  }

  private get ctaVariant() {
    return this.variant === "default" ? "bare" : "tertiary";
  }

  private get isDefault() {
    return this.variant === "default";
  }

  private get isTransparent() {
    return this.variant === "transparent";
  }

  get showHeader() {
    return this.slots.includes("action") || this.title || this.description;
  }

  get showFooter() {
    return this.slots.includes("footer") || this.ctaLabel;
  }

  private onCtaClick() {
    let event: CtaEvent = new CustomEvent("cta", {
      detail: {
        ctaHref: this.ctaHref,
        ctaLabel: this.ctaLabel,
      },
    });
    this.dispatchEvent(event);
  }

  @wire(ResizeObserverAdapter, { template: "$template" })
  private onResize(entry: ResizeObserverEntry) {
    this.width = entry.contentRect.width;
    this.size = this.width < PAGE_SIDEBAR_WIDTH ? "small" : "default";
  }

  private size: "default" | "small" = "default";
  private slots: string = "";

  private onSlotChange() {
    this.slots = assignedSlotNames(this.template).join(" ");
  }

  @wire(EffectAdapter, { variant: "$variant" })
  private onUpdateVariant() {
    this.template.host.setAttribute("data-variant", this.variant);
  }
}
